import React from 'react';
import './styles.scss';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  setCurrentPage,
}) => {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const leftSiblingIndex = Math.max(currentPage - 2, 1); // Show 2 pages before the current
    const rightSiblingIndex = Math.min(currentPage + 2, totalPages); // Show 2 pages after the current

    // Add the first page
    if (currentPage > 3) {
      pageNumbers.push(
        <li key={1} className="govuk-pagination__item">
          <button
            type="button"
            className="govuk-link govuk-pagination__link"
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        </li>
      );
      // Add left ellipsis
      if (currentPage > 4) {
        pageNumbers.push(
          <li key="left-ellipsis" className="govuk-pagination__item">
            <span className="govuk-pagination__link">...</span>
          </li>
        );
      }
    }

    // Display the range of pages around the current page
    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i += 1) {
      pageNumbers.push(
        <li
          key={i}
          className={`govuk-pagination__item ${currentPage === i ? 'govuk-pagination__item--current' : ''}`}
        >
          <button
            type="button"
            className={`govuk-link govuk-pagination__link ${currentPage === i ? 'current-page-button' : ''}`}
            aria-current={currentPage === i ? 'page' : undefined}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    // Add the last page
    if (currentPage < totalPages - 2) {
      // Add right ellipsis
      if (currentPage < totalPages - 3) {
        pageNumbers.push(
          <li key="right-ellipsis" className="govuk-pagination__item">
            <span className="govuk-pagination__link">...</span>
          </li>
        );
      }
      pageNumbers.push(
        <li key={totalPages} className="govuk-pagination__item">
          <button
            type="button"
            className="govuk-link govuk-pagination__link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageNumbers;
  };

  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-end mt-5">
      <nav
        className="govuk-pagination govuk-!-padding-right-2 govuk-!-margin-bottom-0"
        aria-label="Pagination"
      >
        {/* Previous button */}
        <div className="govuk-pagination__prev">
          <button
            type="button"
            className="govuk-link govuk-pagination__link"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <svg
              className="govuk-pagination__icon govuk-pagination__icon--prev"
              xmlns="http://www.w3.org/2000/svg"
              height="13"
              width="15"
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 15 13"
            >
              <path d="m6.5938-0.0078125-6.7266 6.7266 6.7441 6.4062 1.377-1.449-4.1856-3.9768h12.896v-2h-12.984l4.2931-4.293-1.414-1.414z" />
            </svg>
            <span className="govuk-pagination__link-title">Previous</span>
          </button>
        </div>

        {/* Page numbers with ellipsis */}
        <ul className="govuk-list govuk-pagination__list">
          {renderPageNumbers()}
        </ul>

        {/* Next button */}
        <div className="govuk-pagination__next">
          <button
            type="button"
            className="govuk-link govuk-pagination__link"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <span className="govuk-pagination__link-title">Next</span>
            <svg
              className="govuk-pagination__icon govuk-pagination__icon--next"
              xmlns="http://www.w3.org/2000/svg"
              height="13"
              width="15"
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 15 13"
            >
              <path d="m8.107-0.0078125-1.4136 1.414 4.2926 4.293h-12.986v2h12.896l-4.1855 3.9766 1.377 1.4492 6.7441-6.4062-6.7246-6.7266z" />
            </svg>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Pagination;
